'use client';

import * as React from 'react';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { useTranslations } from 'next-intl';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { getBaseUiChildren, getBaseUiRenderProp } from '@/components/ui/base-ui-utils';

const Dialog = BaseDialog.Root;
const DialogPortal = BaseDialog.Portal;
const DialogTitle = BaseDialog.Title;
const DialogDescription = BaseDialog.Description;

type DialogTriggerProps = React.ComponentPropsWithoutRef<typeof BaseDialog.Trigger> & {
  asChild?: boolean;
  className?: string;
};

const DialogTrigger = React.forwardRef<HTMLButtonElement, DialogTriggerProps>(
  ({ asChild = false, children, ...props }, ref) => (
    <BaseDialog.Trigger ref={ref} render={getBaseUiRenderProp(asChild, children)} {...props}>
      {getBaseUiChildren(asChild, children)}
    </BaseDialog.Trigger>
  ),
);
DialogTrigger.displayName = 'DialogTrigger';

type DialogCloseProps = React.ComponentPropsWithoutRef<typeof BaseDialog.Close> & {
  asChild?: boolean;
  className?: string;
};

const DialogClose = React.forwardRef<HTMLButtonElement, DialogCloseProps>(
  ({ asChild = false, children, ...props }, ref) => (
    <BaseDialog.Close ref={ref} render={getBaseUiRenderProp(asChild, children)} {...props}>
      {getBaseUiChildren(asChild, children)}
    </BaseDialog.Close>
  ),
);
DialogClose.displayName = 'DialogClose';

const DialogOverlay = React.forwardRef<
  HTMLDivElement,
  Omit<React.ComponentPropsWithoutRef<typeof BaseDialog.Backdrop>, 'className'> & { className?: string }
>(({ className, ...props }, ref) => (
  <BaseDialog.Backdrop
    ref={ref}
    className={cn('zen-layer-overlay fixed inset-0 bg-black/45 backdrop-blur-sm duration-300', className)}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

type CompatDismissHandler = {
  preventDefault: () => void;
  defaultPrevented: boolean;
};

type DialogContentProps = React.ComponentPropsWithoutRef<typeof BaseDialog.Popup> & {
  hideClose?: boolean;
  className?: string;
  onEscapeKeyDown?: (event: CompatDismissHandler) => void;
  onPointerDownOutside?: (event: CompatDismissHandler) => void;
  onInteractOutside?: (event: CompatDismissHandler) => void;
  onOpenAutoFocus?: (event: CompatDismissHandler) => void;
  onCloseAutoFocus?: (event: CompatDismissHandler) => void;
};

const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  (
    {
      className,
      children,
      hideClose = false,
      onEscapeKeyDown: _onEscapeKeyDown,
      onPointerDownOutside: _onPointerDownOutside,
      onInteractOutside: _onInteractOutside,
      onOpenAutoFocus: _onOpenAutoFocus,
      onCloseAutoFocus: _onCloseAutoFocus,
      ...props
    },
    ref,
  ) => {
    const t = useTranslations('common.dialog');
    return (
      <DialogPortal>
        <DialogOverlay />
        <BaseDialog.Popup
          ref={ref}
          className={cn(
            'zen-layer-modal fixed left-1/2 top-1/2 grid w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 rounded-control border border-zen-border-overlay bg-zen-surface-overlay p-6 text-zen-text shadow-zen-modal outline-none backdrop-blur duration-300',
            className,
          )}
          {...props}
        >
          {children}
          {!hideClose && (
            <BaseDialog.Close
              className="absolute right-4 top-4 inline-flex size-8 items-center justify-center rounded-full border border-zen-border bg-zen-surface/92 text-zen-text-subtle shadow-sm backdrop-blur-sm transition-colors hover:bg-zen-surface hover:text-zen-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zen-ring"
              aria-label={t('close')}
            >
              <X className="size-4" />
            </BaseDialog.Close>
          )}
        </BaseDialog.Popup>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = 'DialogContent';

const DialogSheetContent = React.forwardRef<
  HTMLDivElement,
  Omit<React.ComponentPropsWithoutRef<typeof BaseDialog.Popup>, 'className'> & { className?: string }
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <BaseDialog.Popup
      ref={ref}
      className={cn(
        'zen-layer-modal fixed inset-y-0 right-0 flex w-full max-w-3xl flex-col border-s border-zen-border-overlay bg-zen-surface-overlay text-zen-text shadow-zen-modal outline-none duration-300',
        className,
      )}
      {...props}
    >
      {children}
    </BaseDialog.Popup>
  </DialogPortal>
));
DialogSheetContent.displayName = 'DialogSheetContent';

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col text-left', className)} {...props} />;
}

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)} {...props} />;
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogSheetContent,
  DialogTitle,
  DialogTrigger,
};
export const Root = Dialog;
export const Trigger = DialogTrigger;
export const Portal = DialogPortal;
export const Overlay = DialogOverlay;
export const Content = DialogContent;
export const Close = DialogClose;
export const Title = DialogTitle;
export const Description = DialogDescription;
